import type {
  EmpathyTourStep,
  GiraffeHintStage,
  NeedsGuesserHintStage,
  OnboardingGoal,
  OnboardingTool,
  QuickTourSection,
} from '../types/onboarding';

// ============================================
// Goal → Tool Mapping
// ============================================

export const GOAL_TO_TOOL_MAP: Record<OnboardingGoal, OnboardingTool> = {
  'communicate-better': 'giraffe-translator',
  'resolve-conflicts': 'giraffe-translator',
  'save-relationship': 'giraffe-translator',
  'empathy-superpower': 'needs-guesser',
  'be-heard': 'needs-guesser',
  'self-transformation': 'needs-guesser',
};

// ============================================
// Goal Labels (shown as chips)
// ============================================

export const GOAL_LABELS: Record<OnboardingGoal, string> = {
  'communicate-better': 'Communicate better with my partner',
  'resolve-conflicts': 'Resolve conflicts',
  'save-relationship': 'Save my relationship',
  'empathy-superpower': 'Achieve empathy superpower',
  'be-heard': 'Be heard',
  'self-transformation': 'Spiritual self-transformation',
};

// ============================================
// Warm Responses (per goal, shown at vulnerability step)
// ============================================

export const VULNERABILITY_RESPONSES: Record<OnboardingGoal, string> = {
  'communicate-better': 'I love that. Wanting to communicate better shows real courage and care.',
  'resolve-conflicts':
    'Conflicts can be overwhelming. The good news? There are simple shifts that change everything.',
  'save-relationship':
    "The fact that you're here shows how deeply you care. That matters. As you will learn in the course, certain communication patterns can make or break a relationship, and the good news is they can be learned.",
  'empathy-superpower':
    'I love that! True empathy is a superpower that has the power to transform every relationship.',
  'be-heard':
    "Understanding, and being understood, is one of the nine universal human needs. You're in the right place.",
  'self-transformation':
    'Understanding yourself is the foundation of real transformation. This course teaches a framework, adaptable to any language, that allows us to move beyond the illusory right-wrong way of seeing self and others and experience a deeper version of who we are.',
};

// ============================================
// Quick Tour Sections
// ============================================

export const QUICK_TOUR_SECTIONS_WITH_TOOLS: QuickTourSection[] = [
  'profile',
  'empy',
  'support-chat',
  'games',
  'empathy',
  'progress',
  'course',
];

export const QUICK_TOUR_SECTIONS_WITHOUT_TOOLS: QuickTourSection[] = [
  'profile',
  'empy',
  'support-chat',
  'tools',
  'games',
  'empathy',
  'progress',
  'course',
];

export const QUICK_TOUR_CONTENT: Record<QuickTourSection, { intro: string; detail: string }> = {
  course: {
    intro:
      'The Course page is your structured learning path through NVC — lessons, quizzes, and step-by-step paths that build on each other. Please click it.',
    detail:
      'Complete lessons and quizzes to master NVC step by step. Each path unlocks the next as you progress.',
  },
  progress: {
    intro:
      'The Progress page tracks everything — lessons completed, streaks, achievements, and your leaderboard ranking. Please click it.',
    detail:
      "Check in here to see how far you've come and what milestones are next. Streaks and achievements keep you motivated!",
  },
  empathy: {
    intro:
      'This is the Empathy button — the heart of EmpathEase. Give or receive on-demand empathy with real people in live video sessions. It unlocks after completing the introductory module!',
    detail: '',
  },
  games: {
    intro:
      'The Games page has fun exercises to practice identifying feelings, needs, and observations — learn NVC by playing! Please click it.',
    detail:
      'Each game targets a different NVC skill. The more you play, the more natural empathic communication becomes.',
  },
  tools: {
    intro:
      'The Tools page houses powerful AI helpers — the Giraffe Translator and Needs Guesser — for practicing NVC in real-life situations. Please click it.',
    detail:
      'Use the Giraffe Translator to transform judgmental language into empathic language, and the Needs Guesser to discover the needs behind difficult experiences.',
  },
  profile: {
    intro:
      'Your Profile page is where you manage your account, subscription, and preferences. Please click it.',
    detail:
      'Update your name, avatar, and settings here. You can also manage your subscription and view your public profile.',
  },
  'support-chat': {
    intro:
      'This is your AI tech support assistant — available anytime you have questions about the app or need help. Please click it.',
    detail:
      "Ask about features, troubleshoot issues, or get quick answers about NVC concepts. It's here whenever you need it.",
  },
  empy: {
    intro:
      "And that's me! I'm Empy, your personal NVC coach. Tap me anytime for guidance, coaching on real situations, or just to practice. Please click me.",
    detail:
      "Whether you're stuck on a conversation, want to practice a concept, or just need someone to talk to about NVC — I'm always here for you.",
  },
};

export const QUICK_TOUR_SELECTORS: Record<QuickTourSection, { mobile: string; desktop: string }> = {
  course: {
    mobile: '[data-onboarding="nav-course"]',
    desktop: '[data-onboarding="nav-course"]',
  },
  progress: {
    mobile: '[data-onboarding="nav-progress"]',
    desktop: '[data-onboarding="nav-progress"]',
  },
  empathy: {
    mobile: '[data-onboarding="nav-empathy"]',
    desktop: '[data-onboarding="nav-empathy"]',
  },
  games: {
    mobile: '[data-onboarding="nav-games"]',
    desktop: '[data-onboarding="nav-games"]',
  },
  tools: {
    mobile: '[data-onboarding="nav-tools"]',
    desktop: '[data-onboarding="nav-tools"]',
  },
  profile: {
    mobile: '[data-onboarding="nav-profile"]',
    desktop: '[data-onboarding="nav-profile"]',
  },
  'support-chat': {
    mobile: '[data-onboarding="support-chat"]',
    desktop: '[data-onboarding="support-chat"]',
  },
  empy: {
    mobile: '[data-onboarding="empy-button"]',
    desktop: '[data-onboarding="empy-button"]',
  },
};

// ============================================
// Empathy Page Tour Steps
// ============================================

export const EMPATHY_TOUR_STEPS: EmpathyTourStep[] = [
  {
    id: 'e-button',
    selector: '[data-onboarding="empathy-e-button"]',
    text: 'Your new feature awaits! Tap the Empathy button to explore On-Demand Empathy.',
    action: 'click',
  },
  {
    id: 'give-empathy',
    selector: '[data-onboarding="empathy-give"]',
    text: "As a giver, you join the queue and get matched with someone who needs support. It's a beautiful way to practice NVC.",
    action: 'next',
  },
  {
    id: 'receive-empathy',
    selector: '[data-onboarding="empathy-receive"]',
    text: "Need someone to listen? Request empathy and we'll connect you with an available giver in a live video session.",
    action: 'next',
  },
  {
    id: 'conversations',
    selector: '[data-onboarding="empathy-conversations"]',
    text: 'After each session, your session history is saved here for reflection.',
    action: 'next',
  },
  {
    id: 'try-it',
    selector: '[data-onboarding="empathy-receive"]',
    text: 'Ready to give it a try? Tap Receive to connect with someone who cares!',
    action: 'click',
  },
];

// ============================================
// Guided Tool Hints
// ============================================

export const GIRAFFE_HINTS: Record<Exclude<GiraffeHintStage, 'done'>, string> = {
  'page-load':
    "Type something you've actually said that didn't land well. Be real — that's where the magic happens.",
  'input-ready': "Whenever you're ready, let's see what's hiding in your words.",
  'analyze-highlight':
    "Click 'Analyze Text' to see if your words contain any language that might be hard to hear.",
  analyzing: '', // rendered as thinking dots in component
  'click-sentence': '', // dynamic text computed in component
  'highlight-sentence': '', // no bubble — pulsing border guides the user
  finalize: '', // no bubble — pulsing border on finalize button
  'card-shown': '', // no bubble — pulsing border on share button
};

export const NEEDS_GUESSER_HINTS: Record<Exclude<NeedsGuesserHintStage, 'done'>, string> = {
  'page-load': 'Briefly describe an experience that was emotionally painful.',
  'input-ready': "Whenever you're ready, let's see what needs are behind your words.",
  'submit-highlight': "Click 'Discover My Needs' to see some guesses about your needs.",
  refine: "Select the needs that resonate with you, then tap 'Visualize'!",
  'refine-select': '',
  visualize:
    'The transformation of right-and-wrong language into an understanding of our needs leads out of judgement and into connection.',
};
